import { useLocation } from '@tanstack/react-router';
import { useEffect } from 'react';
import { saveAttributionTouches } from '@/api/attribution';
import { authClient } from '@/auth/client';
import {
  appendAttributionTouch,
  createAttributionTouch,
  getPendingAttributionTouches,
  markAttributionTouchesSynced,
  readAttributionState,
  writeAttributionState,
} from './client';

export function AttributionTracker() {
  const location = useLocation();
  const { data: session } = authClient.useSession();
  const userId = session?.user?.id;

  useEffect(() => {
    let cancelled = false;
    const now = Date.now();
    const currentUrl = new URL(
      `${location.pathname}${location.searchStr || ''}`,
      window.location.origin
    );
    const touch = createAttributionTouch({
      capturedAt: now,
      referrer: document.referrer,
      touchId: createTouchId(),
      url: currentUrl,
    });
    const storedState = readAttributionState(window.localStorage, now);
    const nextState = touch
      ? appendAttributionTouch(storedState, touch, now).state
      : storedState;

    writeAttributionState(window.localStorage, nextState);
    if (!userId) return;

    const pendingTouches = getPendingAttributionTouches(nextState, now);
    if (pendingTouches.length === 0) return;

    saveAttributionTouches({ data: { touches: pendingTouches } })
      .then(({ acceptedTouchIds }) => {
        if (cancelled) return;
        const latestState = readAttributionState(window.localStorage);
        writeAttributionState(
          window.localStorage,
          markAttributionTouchesSynced(latestState, acceptedTouchIds)
        );
      })
      .catch(() => {
        // Keep pending touches for the next authenticated page view.
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.searchStr, userId]);

  return null;
}

function createTouchId() {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
